<template>
    <div>
        <rkm-form
            v-if="model"
            ref="RkmForm"
            show-legend
        >
            <rkm-area
                no-margin-top
                title="Identificação do paciente"
            >
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-input-autocomplete
                            :repository="repositories.recepcao.paciente"
                            :request-query-string-params="{with: ['classificacao', 'unidade']}"
                            :search-fields="fields.autocomplete"
                            :text-field="value => `${value.nome}${value.nomeSocial ? ` (${value.nomeSocial})` : ''}${value.cns ? ' - ' + value.cns : ''}`"
                            :validations="validations.paciente"
                            @select="getUnidadePaciente"
                            label="Nome, nome social, data de nascimento (DD/MM/AAAA) ou prontuário"
                            uppercase
                            v-model="model.paciente"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.nomeSocial)"
                            disabled
                            label="Nome social"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.dataNascimento)"
                            disabled
                            label="Data de nascimento"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.idade)"
                            disabled
                            label="Idade"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row v-if="model.paciente">
                    <rkm-col cols="8">
                        <span
                            v-if="model.paciente.obitoEm"
                            class="mr-1"
                            title="Paciente em óbito"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="cross" />
                        </span>

                        <span
                            v-if="model.paciente.bloqueado"
                            class="mr-1"
                            title="Cadastro bloqueado"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="ban" />
                        </span>

                        <span
                            v-if="isProvisorioOuTemporario"
                            class="mr-1"
                            title="Paciente provisório/temporário"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="hourglass-half" />
                        </span>

                        <span
                            v-if="model.paciente.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                            class="mr-1"
                            title="Paciente não munícipe"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="map-pin" />
                        </span>

                        <span
                            v-if="!model.paciente.cpf && !model.paciente.cns"
                            class="mr-1"
                            title="Paciente sem CPF e CNS"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="exclamation-triangle" />
                        </span>

                        <span
                            v-if="model.paciente.pacienteResponsavelFamiliar"
                            class="mr-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="model.paciente.idadeAnos >= 60"
                            class="mr-1"
                            title="Idoso"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="blind" />
                        </span>

                        <span
                            v-if="hasTea"
                            class="mr-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="hasPcd"
                            class="mr-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="situacaoPaciente"
                            disabled
                            label="Situação"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.prontuario)"
                            disabled
                            label="Prontuário municipal"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cpf)"
                            disabled
                            label="CPF"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cns)"
                            disabled
                            label="CNS"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.nomeMae)"
                            disabled
                            label="Nome da mãe"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.telefoneNumero)"
                            disabled
                            label="Telefone"
                        >
                            <rkm-input-group-append slot="append">
                                <rkm-button
                                    :disabled="!model.paciente"
                                    @click="$refs.ModalPacienteTelefone.open(model.paciente)"
                                    color="primary"
                                    title="Alterar"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="pen" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="8">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.unidadeReferencia)"
                            disabled
                            label="Unidade de referência"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.classificacao)"
                            disabled
                            label="Classificação"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.validadeCadastro)"
                            disabled
                            label="Validade do cadastro"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.endereco)"
                            disabled
                            label="Endereço"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :disabled="!model.paciente"
                            :label="false"
                            @click="handleAtualizarCadastroClick"
                            color="primary"
                        >
                            Atualizar cadastro
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-row>
                <rkm-col>
                    <rkm-form-select
                        :disabled="!!filaId"
                        :options="form.opcoesEntrada"
                        :validations="validations.opcaoEntrada"
                        label="Opção de entrada"
                        show-default-option
                        text-field="nome"
                        v-model="model.opcaoEntradaId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-select
                        :options="form.prioridades"
                        :validations="validations.prioridade"
                        label="Prioridade"
                        show-default-option
                        text-field="nome"
                        v-model="model.prioridadeId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-radio-group
                        :disabled="!model.paciente"
                        :options="form.demandaTipos"
                        :validations="validations.demandaTipo"
                        @change="handleDemandaTipoChange"
                        label="Tipo de demanda"
                        v-model="model.demandaTipo"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="!!model.demandaTipo">
                <rkm-col cols="12">
                    <rkm-form-radio-group
                        :options="atendimentoTipos"
                        :validations="validations.atendimentoTipo"
                        label="Tipo de atendimento"
                        text-field="nome"
                        v-model="model.atendimentoTipoId"
                        value-field="id"
                        class="tipo-atendimento"
                    >
                        <template
                            v-if="isOcupacaoOdontologica"
                            v-slot:label-right
                        >
                            <span class="red ml-1">
                                <template v-if="model.demandaTipo === enums.demandaTipo.AGENDADA">
                                    Para atendimento odontológico, selecione "Consulta agendada".
                                </template>

                                <template v-else>
                                    Para atendimento odontológico, selecione "Escuta inicial/Orientação", "Consulta no dia" ou "Atendimento de urgência".
                                </template>
                            </span>
                        </template>
                    </rkm-form-radio-group>
                </rkm-col>
            </rkm-row>

            <template v-if="model.demandaTipo === enums.demandaTipo.AGENDADA">
                <rkm-area
                    no-margin-top
                    title="Agendamentos do dia"
                    type="fieldset"
                >
                    <rkm-table
                        :fields="fields.table"
                        :items="form.agendamentos"
                        sort-by="horarioAgendado"
                    >
                        <template v-slot:cell(radio)="{item}">
                            <rkm-form-radio
                                :value="item.id"
                                name="agendamento"
                                v-model="model.agendamentoId"
                            />
                        </template>
                    </rkm-table>
                </rkm-area>
            </template>

            <template v-else-if="model.demandaTipo === enums.demandaTipo.ESPONTANEA">
                <rkm-row>
                    <rkm-col>
                        <rkm-form-select
                            :options="form.usuarios"
                            :validations="validations.usuario"
                            @change="handleUsuarioChange"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="form.ocupacoes"
                            :validations="validations.ocupacao"
                            @change="handleOcupacaoChange"
                            label="CBO"
                            show-default-option
                            text-field="descricao"
                            v-model="model.ocupacaoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="form.especialidades"
                            :validations="validations.especialidade"
                            @change="handleEspecialidadeChange"
                            label="Especialidade"
                            show-default-option
                            text-field="nome"
                            v-model="model.especialidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col v-if="demandaEspontaneaObservaVagaAtendimento">
                        <rkm-form-select
                            :options="form.jornadas"
                            :validations="validations.jornada"
                            label="Jornada"
                            show-default-option
                            text-field="descricao"
                            v-model="model.jornadaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>
            </template>

            <rkm-row v-if="form.servicos.length > 0">
                <rkm-col>
                    <rkm-form-checkbox-group
                        :options="form.servicos"
                        label="Serviços"
                        text-field="nome"
                        v-model="model.servicos"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :options="form.centrosCustoEncaminhamento"
                        :validations="validations.centroCustoEncaminhamento"
                        label="Encaminhamento"
                        show-default-option
                        text-field="setorNome"
                        v-model="model.centroCustoEncaminhamentoId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <ModalPacienteTelefone ref="ModalPacienteTelefone" />

        <ModalImpressao
            @closed="closedModalImpressao"
            ref="ModalImpressao"
        />

        <ModalSupervisor
            @authorized="handleAuthorizedSupervisor"
            ref="ModalSupervisor"
        />
    </div>
</template>

<script>
// Components.
import ModalImpressao from '@/components/ambulatorio/components/impressao/Modal'
import ModalPacienteTelefone from '@/components/ambulatorio/components/paciente/telefone/Modal'
import ModalSupervisor from '@/components/ambulatorio/components/supervisor/Modal'

// Enums.
import demandaTipo from '@/enums/ambulatorio/demandaTipo'
import prioridadeTipo from '@/enums/painel-senha/prioridadeTipo'
import servicoTipo from '@/enums/ambulatorio/servicoTipo'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'
import tipoAtendimento from '@/enums/atencao-basica/atendimento-individual/tipoAtendimento'

// Mixins.
import {error, object, permissao, repository, stringCase, unmask} from '@/mixins'

// Vuex.
import {mapActions, mapGetters} from 'vuex'

export default {
    components: {
        ModalImpressao,
        ModalPacienteTelefone,
        ModalSupervisor,
    },
    computed: {
        validations() {
            return {
                paciente: {required: true},
                opcaoEntrada: {required: true},
                prioridade: {
                    required:
                        !!this.model.opcaoEntradaId &&
                        this.form.opcoesEntrada.find(opcaoEntrada => opcaoEntrada.id === this.model.opcaoEntradaId).prioridadeObrigatoria
                },
                demandaTipo: {required: true},
                atendimentoTipo: {required: true},
                centroCustoEncaminhamento: {required: true},
                especialidade:{required: !!this.model.usuarioId},
                jornada:{required: !!this.model.especialidadeId},
            }
        },
        atendimentoTipos() {
            if (!this.model.demandaTipo) {
                return []
            }

            if (this.model.demandaTipo === this.enums.demandaTipo.ESPONTANEA) {
                const tiposValidos = [
                    this.enums.tipoAtendimento.ESCUTA_INICIAL_ORIENTACAO,
                    this.enums.tipoAtendimento.CONSULTA_NO_DIA,
                    this.enums.tipoAtendimento.ATENDIMENTO_DE_URGENCIA,
                ]

                return this.form.atendimentoTipos.filter(t => t.isDemandaEspontanea).map(tipo => ({
                    ...tipo,
                    disabled: this.isOcupacaoOdontologica ? !tiposValidos.includes(tipo.id) : false
                }))
            }

            const tiposValidos = [this.enums.tipoAtendimento.CONSULTA_AGENDADA]

            return this.form.atendimentoTipos.filter(t => !t.isDemandaEspontanea).map(tipo => ({
                ...tipo,
                disabled: this.isOcupacaoOdontologica ? !tiposValidos.includes(tipo.id) : false
            }))
        },
        demandaEspontaneaObservaVagaAtendimento() {
            return this.form.especialidades.find(especialidade => especialidade.id === this.model.especialidadeId)?.demandaEspontaneaObservaVagaAtendimento === true
        },
        isOcupacaoOdontologica() {
            if (!this.model.demandaTipo) {
                return false
            }

            if (this.model.demandaTipo === this.enums.demandaTipo.AGENDADA) {
                if (!this.model.agendamentoId) {
                    return false
                }

                return this.form.agendamentos.find(a => a.id === this.model.agendamentoId).isOdontologico
            }

            if (!this.model.ocupacaoId) {
                return false
            }

            return this.form.ocupacoes.find(o => o.id === this.model.ocupacaoId).isOdontologico
        },
        isProvisorioOuTemporario() {

            return this.model.paciente.classificacaoId == this.enums.classificacao.PROVISORIO
                || this.model.paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        deficienciasBaseIds() {

            return this.model.paciente.deficienciasBaseIds
                ? this.model.paciente.deficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea() {

            return this.deficienciasBaseIds.includes(this.enums.deficienciaBase.TEA)
                || this.model.paciente.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd() {

            return this.deficienciasBaseIds.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.model.paciente.deficienciaId && this.model.paciente.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        situacaoPaciente() {
            if (this.model.paciente.obitoEm) {
                return 'Paciente em óbito'
            }
            if (this.model.paciente.bloqueado) {
                return 'Cadastro bloqueado'
            }
            if (this.isProvisorioOuTemporario) {
                return 'Paciente provisório ou temporário'
            }
            if (this.model.paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Paciente não munícipe'
            }
            if (!this.model.paciente.cpf && !this.model.paciente.cns) {
                return 'Paciente sem CPF e CNS'
            }

            return 'Normal'
        },
    },
    created() {
        this.repositories = {
            recepcao: this.getConectaSusRepository('ambulatorio.recepcao'),
        }
    },
    data: () => ({
        additionals: null,
        enums: {
            demandaTipo,
            prioridadeTipo,
            servicoTipo,
            classificacao,
            subclassificacao,
            deficienciaBase,
            deficiencia,
            tipoAtendimento,
        },
        fields: {
            autocomplete: [
                {key: 'cidadao.nome', searchable: true},
                {key: 'cidadao.data_nascimento', searchable: true},
                {key: 'paciente.prontuario', searchable: true},
            ],
            table: [
                {key: 'radio', label: '#', class: 'col-min text-center'},
                {key: 'horario', label: 'Horário', sortable: true},
                {key: 'ocupacaoDescricao', label: 'CBO', sortable: true},
                {key: 'especialidadeNome', label: 'Especialidade', sortable: true},
                {key: 'usuarioNome', label: 'Profissional', sortable: true},
            ],
        },
        form: null,
        model: null,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        ...mapGetters(['getLotacao']),
        load() {
            this.create()
        },
        create() {
            this.setLoading()

            this.repositories.recepcao.create(this.filaId)
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)

                    if (this.additionals.hasAtendimento === true) {
                        this.$emit('redirect')
                    } else {
                        this.$emit('loaded', {additionals: this.additionals, form: this.form})
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        store() {
            this.setLoading()

            const request = {
                filaId: this.filaId,
                atendimentoTipoId: this.model.atendimentoTipoId,
                pacienteId: this.model.paciente.id,
                opcaoEntradaId: this.model.opcaoEntradaId,
                prioridadeId: this.model.prioridadeId,
                agendamentoId: this.model.agendamentoId,
                ocupacaoId: this.model.ocupacaoId,
                especialidadeId: this.model.especialidadeId,
                usuarioId: this.model.usuarioId,
                jornadaId: this.model.jornadaId,
                servicos: this.model.servicos,
                centroCustoEncaminhamentoId: this.model.centroCustoEncaminhamentoId,
                paciente: {
                    telefonePrincipal: {
                        id: this.model.paciente.telefoneId,
                        numero: this.model.paciente.telefoneNumero ? this.unMaskPhone(this.model.paciente.telefoneNumero) : null,
                        autorizaSms: this.model.paciente.telefoneAutorizaSms,
                    },
                },
            }

            this.repositories.recepcao.store(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.openModalImpressao(response.data.id))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (this.model.demandaTipo === this.enums.demandaTipo.AGENDADA && !this.model.agendamentoId) {
                        this.showValidationNotification('É obrigatório escolher um agendamento.')
                        return
                    }

                    if (this.demandaEspontaneaObservaVagaAtendimento) {
                        const jornada = this.form.jornadas.find(jornada => jornada.id === this.model.jornadaId)

                        if (jornada && jornada.quantidadeUtilizada >= jornada.quantidadeConsulta) {
                            this.$refs.ModalSupervisor.open({infraestruturaId: this.getLotacao().infraestruturaId, lotacaoInfraestruturaId: this.getLotacao().infraestruturaId})
                        } else {
                            this.store()
                        }
                    } else {
                        this.store()
                    }
                })
        },
        openModalImpressao(atendimentoId) {
            this.$refs.ModalImpressao.open('recepcao', atendimentoId)
        },
        closedModalImpressao() {
            this.showSaveNotification()
            this.$emit('saved')
        },
        handlePacienteSelect(value) {
            this.model.paciente = value

            if (value.idadeAnos >= 60) {
                const prioridade = this.form.prioridades.find(prioridade => prioridade.tipoId === this.enums.prioridadeTipo.IDOSO)

                if (prioridade) {
                    this.model.prioridadeId = prioridade.id
                }
            }

            this.$emit('selected', this.model.paciente)
        },
        async getUnidadePaciente(value) {

            this.setLoading()

            try {
                const {data} = await this.repositories.recepcao.unidadePaciente(value.id)

                const selectPaciente = () => {
                    this.handlePacienteSelect(value)
                    this.showAlertaCadastroDesatualizado(data)
                }

                if (data.enderecoDesatualizadoBloqueado) {

                    this.showAlert(
                        'O endereço do paciente ainda não foi atualizado pela Base Nacional. Atualize o cadastro para continuar.',
                        'Atenção'
                    )
                    this.model.paciente = null
                }
                else if (!data.cnsValido && data.avisaCnsTemporarioInvalido) {

                    this.showWarningNotification('Paciente com CNS inválido. Agendamento não permitido.')
                    this.model.paciente = null
                }
                else if (value.subclassificacaoId === this.enums.subclassificacao.TEMPORARIO
                    && data.avisaCnsTemporarioInvalido) {

                        this.showConfirmation(
                            `Paciente com cadastro temporário, deseja continuar com o agendamento?`,
                            async () => selectPaciente(),
                            async () => this.model.paciente = null
                        )
                }
                else if (data.unidadeComJornada) {

                    this.showConfirmation(
                        `Paciente pertence à unidade ${data.unidade}, deseja continuar com o agendamento?`,
                        async () => selectPaciente(),
                        async () => this.model.paciente = null
                    )
                } else {

                    selectPaciente()
                }
            }
            catch (error) {
                this.handleError(error)
            }
            finally {
                this.unsetLoading()
            }
        },
        showAlertaCadastroDesatualizado(data) {

            if (!data.diasAtrasados) return

            let message = `Cadastro desatualizado há ${data.diasAtrasados} ${data.diasAtrasados > 1 ? 'dias' : 'dia'}.`

            if (!data.enderecoAtualizadoBaseNacional) {

                message += ' Endereço do usuário também deve ser atualizado.'
            }

            this.showAlert(message, 'Atenção')
        },
        handleDemandaTipoChange(value) {
            this.form.agendamentos = []
            this.form.usuarios = []
            this.form.ocupacoes = []
            this.form.especialidades = []
            this.form.jornadas = []
            this.model.agendamentoId = null
            this.model.usuarioId = null
            this.model.ocupacaoId = null
            this.model.especialidadeId = null
            this.model.jornadaId = null
            this.model.servicos = []

            if (value === this.enums.demandaTipo.AGENDADA) {
                this.getAgendamentos(this.model.paciente.id)
            }
            else if (value === this.enums.demandaTipo.ESPONTANEA) {
                this.getUsuarios(this.model.paciente.id)
            }
        },
        handleUsuarioChange(value) {
            this.form.ocupacoes = []
            this.form.especialidades = []
            this.form.jornadas = []
            this.model.ocupacaoId = null
            this.model.especialidadeId = null
            this.model.jornadaId = null

            if (value === null) return

            this.getOcupacoes(this.model.paciente.id, value)
        },
        handleOcupacaoChange(value) {
            this.validateTipoAtendimento()

            this.form.especialidades = []
            this.form.jornadas = []
            this.model.especialidadeId = null
            this.model.jornadaId = null

            if (value === null) return

            this.getEspecialidades(this.model.paciente.id, this.model.usuarioId, value)
        },
        handleEspecialidadeChange(value) {
            this.form.jornadas = []
            this.model.jornadaId = null

            if (value === null) return

            this.getJornadas(this.model.paciente.id, this.model.usuarioId, this.model.ocupacaoId, value)
        },
        getAgendamentos(pacienteId) {
            const request = {
                listar: 'agendamentos',
                pacienteId,
            }

            this.setLoading()

            this.repositories.recepcao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.agendamentos = Object.assign(response.data.agendamentos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getUsuarios(pacienteId) {
            const request = {
                listar: 'usuarios',
                pacienteId,
            }

            this.setLoading()

            this.repositories.recepcao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.usuarios = Object.assign(response.data.usuarios)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getOcupacoes(pacienteId, usuarioId) {
            const request = {
                listar: 'ocupacoes',
                pacienteId,
                usuarioId,
            }

            this.setLoading()

            this.repositories.recepcao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.ocupacoes = Object.assign(response.data.ocupacoes)

                    if (this.form.ocupacoes.length === 1) {
                        const ocupacaoId = this.form.ocupacoes[0].id

                        this.model.ocupacaoId = ocupacaoId

                        this.getEspecialidades(pacienteId, this.model.usuarioId, ocupacaoId)
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getEspecialidades(pacienteId, usuarioId, ocupacaoId) {
            const request = {
                listar: 'especialidades',
                pacienteId,
                usuarioId,
                ocupacaoId,
            }

            this.setLoading()

            this.repositories.recepcao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.especialidades = Object.assign(response.data.especialidades)

                    if (this.form.especialidades.length === 1) {
                        this.model.especialidadeId = this.form.especialidades[0].id

                        this.getJornadas(pacienteId, usuarioId, ocupacaoId, this.model.especialidadeId)
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getJornadas(pacienteId, usuarioId, ocupacaoId, especialidadeId) {
            const request = {
                listar: 'jornadas',
                pacienteId,
                usuarioId,
                ocupacaoId,
                especialidadeId,
            }

            this.setLoading()

            this.repositories.recepcao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.jornadas = Object.assign(response.data.jornadas.jornadas)

                    const jornadaId = response.data.jornadas.jornadaId

                    if (jornadaId) {
                        this.model.jornadaId = jornadaId
                    }
                    else if (this.form.jornadas.length === 1) {
                        this.model.jornadaId = this.form.jornadas[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleAtualizarCadastroClick() {
            const route = this.$router.resolve({name: 'cadastro-paciente-edit', params: {id: this.model.paciente.id}})

            window.open(route.href, '_blank')
        },
        handleAuthorizedSupervisor() {
            this.store()
        },
        validateTipoAtendimento() {
            if (!this.model.atendimentoTipoId || !this.isOcupacaoOdontologica) return

            let tiposValidos = []

            if (this.model.demandaTipo === this.enums.demandaTipo.AGENDADA) {
                tiposValidos = [this.enums.tipoAtendimento.CONSULTA_AGENDADA]
            }
            else {
                tiposValidos = [
                    this.enums.tipoAtendimento.ESCUTA_INICIAL_ORIENTACAO,
                    this.enums.tipoAtendimento.CONSULTA_NO_DIA,
                    this.enums.tipoAtendimento.ATENDIMENTO_DE_URGENCIA,
                ]
            }

            if (!tiposValidos.includes(this.model.atendimentoTipoId)) {
                this.model.atendimentoTipoId = null

                this.showWarningNotification('O tipo de atendimento selecionado não é permitido para atendimento odontológico. Selecione uma opção válida.')
            }
        }
    },
    mixins: [error, object, permissao, repository, stringCase, unmask],
    mounted() {
        this.load()
    },
    props: {
        filaId: {type: Number},
    },
    watch: {
        'model.paciente'() {
            this.form.agendamentos = []
            this.form.usuarios = []
            this.form.ocupacoes = []
            this.form.especialidades = []
            this.form.jornadas = []
            this.model.demandaTipo = null
            this.model.agendamentoId = null
            this.model.usuarioId = null
            this.model.ocupacaoId = null
            this.model.especialidadeId = null
            this.model.jornadaId = null
        },
        'model.demandaTipo'(value) {
            if (value === this.enums.demandaTipo.ESPONTANEA) {
                this.model.atendimentoTipoId = this.enums.tipoAtendimento.ESCUTA_INICIAL_ORIENTACAO
            } else {
                this.model.atendimentoTipoId = this.enums.tipoAtendimento.CONSULTA_AGENDADA
            }
        },
        'model.agendamentoId'() {
            this.validateTipoAtendimento()
        }
    },
}
</script>

<style scoped>
.tipo-atendimento >>> .red {
    color: var(--danger);
}
</style>